<script setup lang="ts">
import { NavigationMenuLink } from '@/components/navigation-menu'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { MenuItem, isLinkMenuItem, isDividerMenuItem, isRouterLinkMenuItem, isMenuItemVisible } from './nav_types'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import RouterLinkMenuItem from './RouterLinkMenuItem.vue'
import AIBetaBadge from '@/components/AIBetaBadge.vue'

// Define component options for dynamic rendering
const LinkComponent = {
  mobile: 'a',
  desktop: NavigationMenuLink
}

const DividerComponent = {
  mobile: 'div',
  desktop: 'hr'
}

const props = defineProps({
  menuItems: {
    type: Array as () => MenuItem[],
    required: true
  },
  isMobile: {
    type: Boolean,
    default: false
  }
})

const analytics = getAnalyticsTracker()

function handleClick(item: MenuItem, event: Event) {
  if ((isRouterLinkMenuItem(item) || isLinkMenuItem(item)) && item.event) {
    analytics.sendEvent(item.event, item.eventParams)
  }

  if (isLinkMenuItem(item) && item.onClick) {
    item.onClick()
    // Only prevent default if it's not an external link
    if (item.target !== '_blank') {
      event.preventDefault()
    }
  }
}
</script>

<template>
  <template v-for="(item, index) in menuItems" :key="index">
    <!-- Router Link Menu Items -->
    <template v-if="isRouterLinkMenuItem(item) && isMenuItemVisible(item)">
      <RouterLinkMenuItem 
        :is-mobile="isMobile"
        :to="item.to" 
        :text="item.text" 
        @click="handleClick(item, $event)"
      />
    </template>
    
    <!-- Link Menu Items -->
    <template v-else-if="isLinkMenuItem(item) && isMenuItemVisible(item)">
      <component
        :is="isMobile ? LinkComponent.mobile : LinkComponent.desktop"
        :class="[
          isMobile ? 'mobile-menu-link' : 'router-link',
          item.showAIBetaBadge ? 'flex items-center gap-2' : ''
        ]"
        :href="item.href"
        @click="handleClick(item, $event)"
        :target="item.target || ''"
      >
        <div v-if="item.external" class="flex items-center justify-between w-full">
          <span>{{ item.text }}</span>
          <OpensInNewTabIcon :external-link="true" />
        </div>
        <template v-else>
          <span>{{ item.text }}</span>
          <AIBetaBadge v-if="item.showAIBetaBadge" />
        </template>
      </component>
    </template>    
    
    <!-- Divider Menu Items -->
    <component 
      v-else-if="isDividerMenuItem(item)"
      :is="isMobile ? DividerComponent.mobile : DividerComponent.desktop"
      :class="isMobile ? 'my-2 border-t border-gray-200' : 'my-2'"
    />
  </template>
</template>
